import { type ChangeEventHandler, ReactElement, useCallback, useMemo } from 'react';
import { DateField, TimeField } from '@flos/react-ui';

import { getDateOnly, getTimeOnly, getTimesOfDay } from 'common/helpers/date';

import FormItem from 'components/FormItem/FormItem';

import styles from './DatePicker.module.css';

/* ============================================ Local Functions ============================================ */
const getDate: TGetDate = (date, time, isReturnNumber) => {
  const formattedDate = getDateOnly(date ?? new Date());
  const formattedFuncLocalValue = new Date(`${formattedDate} ${time}`);
  let finalFuncLocalValue = isReturnNumber ? formattedFuncLocalValue.getTime() : formattedFuncLocalValue;

  return finalFuncLocalValue;
};

/* ============================================ Main Component ============================================ */
const DatePicker: TDatePicker = ({
  className,
  label,
  value,
  minDate,
  hasTime = false,
  isRequired,
  onChange = () => {}
}) => {
  const [date, time] = useMemo(() => {
    const formattedValue = value ? new Date(value) : new Date();

    const date = getDateOnly(formattedValue);
    const time = getTimeOnly(formattedValue, { gapInMinutes: 5 });

    return [date, time];
  }, [value]);

  const timesOfDay = useMemo(() => {
    const timesOfDay = hasTime ? getTimesOfDay() : [];
    const timesOfDayOptions = timesOfDay.map((timeOfDay) => ({ value: timeOfDay, label: timeOfDay }));
    return timesOfDayOptions;
  }, [hasTime]);

  const handleOnDateChange: THandleOnDateChange = useCallback(
    (funcLocalValue) => {
      const finalFuncLocalValue = getDate(funcLocalValue, time, typeof value === 'number');

      onChange(finalFuncLocalValue);
    },
    [value, time, onChange]
  );

  const handleOnTimeChange: THandleOnTimeChange = useCallback(
    (e) => {
      const funcLocalValue = e.target.value;
      const finalFuncLocalValue = getDate(date, funcLocalValue, typeof value === 'number');

      onChange(finalFuncLocalValue);
    },
    [value, date, onChange]
  );

  return (
    <FormItem className={className} label={label} isRequired={isRequired}>
      <div className={styles['root']}>
        <DateField label="dato" value={date} minDate={minDate} onChange={handleOnDateChange} />
        {hasTime && (
          <TimeField
            wrapperClassName={styles['time']}
            label="tid"
            value={time}
            options={timesOfDay}
            onChange={handleOnTimeChange}
          />
        )}
      </div>
    </FormItem>
  );
};

export default DatePicker;

/* ============================================ Local Types ============================================ */
type TGetDate = (date: Date | string | null, time: string, isReturnNumber: boolean) => number | Date;

type THandleOnDateChange = (value: Date | null) => void;

type THandleOnTimeChange = ChangeEventHandler<HTMLInputElement>;

type TDatePicker = (props: {
  className?: string;
  label: string;
  value?: number | Date;
  minDate?: Date;
  hasTime?: boolean;
  isRequired?: boolean;
  onChange?: (value: any) => void;
}) => ReactElement;
